/**
 * Argent — convention BoutikOS
 *
 * Règles non-négociables :
 * - JAMAIS de float. Toujours des centimes FCFA en `number` (entier).
 * - `formatFcfa()` est la seule façon d'afficher.
 * - Toutes les opérations arithmétiques passent par `fcfa()` pour garantir l'arrondi.
 *
 * FCFA = franc CFA BCEAO (XOF). Pas de subdivision — tout est en nombre entier de francs.
 * On garde quand même le suffixe `Centimes` au cas où on ajoute une autre devise.
 */

export type Centimes = number;
export type Fcfa = number; // alias sémantique pour FCFA entier

/** Convertit n'importe quelle valeur en FCFA entier (arrondi bancaire). */
export const fcfa = (n: number): Fcfa => {
  if (!Number.isFinite(n)) {
    throw new DomainError('argent_invalide', `fcfa() reçoit un non-fini: ${n}`);
  }
  return Math.round(n);
};

/** Addition sûre (jamais de float fantôme). */
export const addFcfa = (...vals: number[]): Fcfa =>
  vals.reduce<number>((acc, v) => acc + fcfa(v), 0);

/** Soustraction sûre. */
export const subFcfa = (a: number, b: number): Fcfa => fcfa(a) - fcfa(b);

/** Multiplication (quantité × prix unitaire). */
export const mulFcfa = (prixUnitaire: number, qte: number): Fcfa =>
  fcfa(fcfa(prixUnitaire) * qte);

/** Pourcentage d'un montant (ex: TVA 18%). */
export const pct = (montant: number, percent: number): Fcfa =>
  fcfa((fcfa(montant) * percent) / 100);

/** Formatage affichage — seule fonction UI peut manipuler du FCFA. */
export const formatFcfa = (c: number): string => {
  const n = fcfa(c);
  return new Intl.NumberFormat('fr-BJ', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n) + ' F';
};

/** Parse une chaîne "1500" ou "1 500" en FCFA entier. */
export const parseFcfa = (s: string): Fcfa | null => {
  const cleaned = s.replace(/[^\d-]/g, '');
  if (cleaned === '' || cleaned === '-') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return fcfa(n);
};

export class DomainError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'DomainError';
  }
}
